/**
 * The app-load overlay — an instant-paint loading screen that covers the initial
 * JS-graph + font + stylesheet fetch, then dissolves into the game-start entrance.
 *
 * Why a loading screen here: everything the game needs before round end — the
 * gameplay JS graph (GSAP + plugins + app), minus the result surface (below) —
 * already loads eagerly before the app runs, so the only asset classes still
 * fetching after this module executes are the Satoshi fonts and, since the
 * early-paint design (todo/initial-load-branded-boot-splash), skat.css itself
 * (loaded non-blocking now — game.html's <link media="print" onload="…">).
 * Gating the reveal on `document.fonts.ready` and skat.css having applied means
 * that by the time the player is past the overlay, nothing the game needs before
 * round end is still in flight — any later stutter is provably render-side,
 * never a load.
 *
 * The named exception: the FINISHED result surface (result-ui.js and its
 * subtree, perf-lazy-result-surface) is dynamically imported, not part of this
 * eager graph — it warms via an idle prefetch scheduled from every live-board
 * render (game.js), so it is normally ready well before a round ends, but a
 * reload landing directly on a finished round can still hit a genuine cold
 * import (game.js#ensureResultUi's cold path).
 *
 * The overlay markup + its base styling live in the Thymeleaf shell + skat.css so
 * they paint on the first frame, before this module (or skat.js) has loaded. This
 * module owns the overlay's lifecycle from there: pin the splash's anchor at boot
 * (pinBootSplash), await readiness, then dissolve into the entrance. Every export
 * no-ops when the overlay is absent (dev / lab pages).
 */
import gsap from "./vendor/gsap.js";

// play-test: the floor the overlay stays up, so a cached/instant load reads as a
// deliberate beat, not a flash. Tuned live (?slowload simulates a slow network).
const MIN_DWELL_MS = 130;
// play-test: the overlay's dissolve. The entrance leads in until this completes
// (plus a brief blank beat) before it begins — exported so the entrance can track
// it, keeping the loader→introduction handoff a clean cut, not a crossfade.
export const DISSOLVE_S = 0.45;

// The overlay paints from server HTML right after navigation start (paint-first-chunked-boot
// design) — this module's own evaluation now runs mid-walk, well after that paint, so stamping
// SHOWN_AT here would undercount how long the player has already watched the splash. 0 (elapsed
// since time origin) is the honest dwell-floor proxy instead: the splash's actual paint time.
const SHOWN_AT = 0;

function overlayEl() {
  return document.getElementById("app-loader");
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ?slowload=<ms> holds the overlay an extra <ms> so the bar can be felt at a
// realistic duration on a fast localhost. Dev-only knob; 0 / absent in prod.
function slowloadMs() {
  const v = new URLSearchParams(location.search).get("slowload");
  const n = v == null ? 0 : Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : 0; // never shorten below the dwell floor
}

// By id, not by href: prod's build content-hashes the stylesheet and the template resolves
// th:href to /assets/css/skat-<HASH>.css, so an href-matching selector would find nothing there
// and silently un-gate the render below (resolve immediately, paint against unstyled DOM). The id
// is game.html's / landing.html's own async <link>, and only that one — the <noscript> twin
// carries none, so this can never latch onto the never-loading fallback.
function skatCssLink() {
  return document.getElementById("skat-css");
}

/**
 * Resolves once skat.css is parsed AND applied to the current media — not just fetched. The
 * early-paint design (todo/initial-load-branded-boot-splash) loads it non-blocking via
 * `<link media="print" onload="this.media='all'">` (game.html); the inline onload flips
 * `media` "print" → "all" the instant it applies, which is the signal this listens for — not
 * `link.sheet`, which populates for a media="print" link too, well before it ever applies to
 * screen rendering. The first render must not run before this: game-start-layout.js measures
 * real geometry, so rendering against unstyled DOM would both flash unstyled content and solve
 * the layout against the wrong sizes. Resolves immediately if the flip already happened before
 * this ran (a fast/cached load racing this module's own execution) rather than waiting on a
 * 'load' event that already fired — the same-tick check-then-listen order below is safe from
 * that race precisely because JS is single-threaded: nothing can fire between them.
 *
 * A load *error* (404, dropped connection) resolves too, revealing with the inlined
 * critical CSS only — degraded but alive, the same "broken styling, working app" the old
 * render-blocking design gave a failed skat.css. Waiting instead would wedge the splash
 * forever: `error` never flips `media`, so no later signal comes (the font gate below
 * makes the same reveal-anyway call).
 */
export function awaitStylesheetReady() {
  const link = skatCssLink();
  if (link == null) return Promise.resolve(); // no non-blocking <link> here (tests, dev pages) — nothing to gate
  if (link.media !== "print") return Promise.resolve(); // already flipped to "all"
  return new Promise((resolve) => {
    link.addEventListener("load", () => resolve(), { once: true });
    link.addEventListener(
      "error",
      () => {
        console.warn(
          "[app-loader] skat.css failed to load — revealing with critical CSS only",
        );
        resolve();
      },
      { once: true },
    );
  });
}

/**
 * Freeze the splash's horizontal anchor as a pixel value, so the one gutter toggle left in the
 * boot window cannot slide it. `.boot-splash` is positioned at `left: 50vw` (skat.css § App
 * Loader) and `vw` tracks a reserved `scrollbar-gutter`, so game.js#clearAppForRender dropping
 * `html.game-start-open` at game entry — which on a resumed-game boot happens while the loader is
 * still fully opaque — would otherwise jump the crest 7.5px at 1280 wide. A px offset resolves
 * against the containing block's own left edge, which the gutter never moves, so the pin survives
 * the release. Only `left` is replaced: the centering `transform` stays, and `top` needs no pin
 * because the gutter narrows the box horizontally only.
 *
 * Must run before the first render, which is the earliest anything touches that class. Called
 * from boot.js#init's first lines rather than on module evaluation — an ordering this load-bearing
 * should be visible at the call site (and eager-module walk order is not a contract).
 *
 * The position read here is already the final one: the splash's rule is inlined in the
 * critical-CSS block, so it holds from the first paint and skat.css landing later re-declares the
 * same `left: 50vw`. Accepted edge: a viewport resize inside the ~700ms boot window will not
 * re-centre the pinned splash — a deliberate trade, not an oversight. Nothing resizes a window
 * while a loading screen is up, and a stale offset on a dissolving overlay is a far smaller cost
 * than the mid-boot jump this prevents.
 */
export function pinBootSplash() {
  const splash = document.querySelector(".boot-splash");
  if (splash == null) return; // no overlay on dev / lab pages — nothing to pin
  splash.style.left = getComputedStyle(splash).left;
}

/**
 * Await everything that could still be fetching before the real screen's first
 * paint: skat.css applying, the Satoshi fonts (the one lazy asset class outside
 * the JS graph), plus the minimum-dwell floor. Resolves when it is honest to
 * reveal the screen.
 */
export async function awaitAppReady() {
  if (overlayEl() == null) return; // no overlay on dev / lab pages — nothing to gate
  await awaitStylesheetReady();
  if (document.fonts != null) {
    try {
      await document.fonts.ready;
    } catch {
      // a font load rejecting must never block boot — reveal anyway
    }
  }
  const elapsed = performance.now() - SHOWN_AT;
  const wait = Math.max(0, MIN_DWELL_MS - elapsed) + slowloadMs();
  if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
}

/**
 * Dissolve the overlay, revealing the screen underneath. Complete the bar to full
 * first (the ready snap), then fade out. Under reduced motion it is removed in one
 * tick. Removes the node when done.
 */
export function dissolveAppLoader() {
  const overlay = overlayEl();
  if (overlay == null) return;
  overlay.classList.add("app-loader-ready"); // CSS completes the bar to 100%
  if (prefersReducedMotion()) {
    overlay.remove();
    return;
  }
  gsap.to(overlay, {
    autoAlpha: 0,
    duration: DISSOLVE_S,
    ease: "power2.inOut",
    onComplete: () => overlay.remove(),
  });
}
